import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";
import type { Movie } from "@/types/movie";

function buildRelated(movie: Movie): Movie[] {
  return movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const sharedGenres = item.genres.filter((genre) => movie.genres.includes(genre)).length;
      const sameDirector = item.director === movie.director ? 3 : 0;
      return { item, score: sharedGenres * 2 + sameDirector };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating)
    .slice(0, 4)
    .map((entry) => entry.item);
}

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [id]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2200);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const related = useMemo(() => (movie ? buildRelated(movie) : []), [movie]);

  useSeo({
    title: movie ? `${movie.title}（${movie.year}）剧情介绍与资源 - 日韩伦理电影` : "影片详情 - 日韩伦理电影",
    description: movie
      ? `${movie.title}（${movie.year}，${movie.country}）由${movie.director}执导，${movie.cast.join("、")}主演。${movie.synopsis[0].slice(0, 72)}...`
      : "日韩伦理电影影片详情页，提供导演、主演、年份、类型与完整剧情介绍。",
    keywords: movie
      ? `${movie.title},${movie.title}剧情介绍,${movie.director},${movie.cast[0]},${movie.genres.join(",")},日韩伦理电影`
      : "日韩伦理电影,影片详情,剧情介绍",
  });

  if (!movie) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-24 text-center md:px-8">
        <i className="ri-film-line text-4xl text-primary-400"></i>
        <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950">没有找到这部影片</h1>
        <p className="mt-2 text-[13px] text-foreground-500">影片可能已下架，或者链接不正确。</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Link to="/" className="rounded-md bg-primary-600 px-4 py-2.5 text-[12.5px] text-background-50">
            返回首页
          </Link>
          <Link
            to="/search?q="
            className="rounded-md border border-background-300/70 px-4 py-2.5 text-[12.5px] text-foreground-700"
          >
            去搜索
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(movie.id);
  const mainGenre = movie.genres[0];
  const categoryInfo = categories.find((item) => item.key === mainGenre);
  const shareUrl = typeof window === "undefined" ? "" : window.location.href;
  const shareTitle = `${movie.title}（${movie.year}）剧情介绍 - 日韩伦理电影`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const metaRows = [
    { label: "上映年份", value: `${movie.year} 年` },
    { label: "制片地区", value: movie.country },
    { label: "影片时长", value: `${movie.duration} 分钟` },
    { label: "影片类型", value: movie.genres.join(" / ") },
  ];

  return (
    <div className="bg-background-50">
      <section className="relative overflow-hidden border-b border-background-200/70">
        <img
          src={movie.cover}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-110 object-cover object-top opacity-30 blur-md"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/85 via-background-50/92 to-background-50"></div>

        <div className="relative mx-auto w-full max-w-[1280px] px-4 pb-10 pt-6 md:px-8 md:pb-14 md:pt-8">
          <nav aria-label="面包屑" className="mb-6 flex flex-wrap items-center gap-1.5 font-label text-[11px] text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-300">首页</Link>
            <i className="ri-arrow-right-s-line text-[13px]"></i>
            <Link to={`/category/${mainGenre}`} className="transition-colors hover:text-primary-300">
              {mainGenre}电影
            </Link>
            <i className="ri-arrow-right-s-line text-[13px]"></i>
            <span className="max-w-[220px] truncate text-primary-300">{movie.title}</span>
          </nav>

          <div className="flex flex-col gap-7 md:flex-row md:items-start md:gap-9">
            <div className="mx-auto w-[190px] shrink-0 md:mx-0 md:w-[248px]">
              <div className="overflow-hidden rounded-lg border border-background-300/70 bg-background-100">
                <img
                  src={movie.cover}
                  alt={`${movie.title}（${movie.year}）电影海报`}
                  title={`${movie.title} ${movie.year} ${movie.genres.join(" ")} 剧情介绍`}
                  className="h-auto w-full object-cover object-top"
                />
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => toggleFavorite(movie.id)}
                  className={`flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2.5 text-[12px] transition-colors ${
                    favorited
                      ? "border border-primary-500 bg-primary-950/60 text-primary-200"
                      : "border border-background-300/70 text-foreground-600 hover:border-primary-500 hover:text-primary-200"
                  }`}
                >
                  <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={copyLink}
                  aria-label="复制页面链接"
                  className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300/70 text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-200"
                >
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/${mainGenre}`}
                  className="rounded-md bg-primary-600 px-2 py-1 font-label text-[10px] font-semibold tracking-wider text-background-50"
                >
                  {mainGenre}
                </Link>
                {movie.genres.slice(1).map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${genre}`}
                    className="rounded-md border border-background-300/70 px-2 py-1 text-[10.5px] text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-200"
                  >
                    {genre}
                  </Link>
                ))}
                <span className="flex items-center gap-1 rounded-md border border-secondary-800/60 bg-secondary-950/40 px-2 py-1 font-label text-[11px] text-secondary-300">
                  <i className="ri-star-fill text-[10px] text-primary-400"></i>
                  {movie.rating.toFixed(1)} / 10
                </span>
              </div>

              <h1 className="font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-[42px]">
                {movie.title}
              </h1>
              <p className="mt-2 font-label text-[12px] tracking-[0.2em] text-foreground-500">{movie.originalTitle}</p>

              <p className="mt-4 max-w-2xl border-l-2 border-primary-600/70 pl-3 text-[13.5px] leading-relaxed text-foreground-600">
                {movie.tagline}
              </p>

              <dl className="mt-6 grid max-w-2xl grid-cols-2 gap-x-6 gap-y-3 border-t border-background-200/70 pt-5 md:grid-cols-4">
                {metaRows.map((row) => (
                  <div key={row.label}>
                    <dt className="font-label text-[10.5px] uppercase tracking-[0.14em] text-foreground-500">{row.label}</dt>
                    <dd className="mt-1 text-[12.5px] text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-5 max-w-2xl space-y-2 text-[12.5px] leading-relaxed text-foreground-600">
                <p>
                  <span className="mr-2 text-foreground-500">导演</span>
                  {movie.director}
                  <span className="ml-5 text-foreground-500">主演</span>
                  <span className="ml-2">{movie.cast.join(" / ")}</span>
                </p>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="mr-1 font-label text-[11px] uppercase tracking-[0.16em] text-foreground-500">分享</span>
                <a
                  href={`https://myhxp26.cn/share/share.php?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-8 items-center gap-1.5 rounded-md border border-background-300/70 px-2.5 font-label text-[11px] text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-200"
                >
                  <i className="ri-weibo-line"></i>
                  微博
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-8 items-center gap-1.5 rounded-md border border-background-300/70 px-2.5 font-label text-[11px] text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-200"
                >
                  <i className="ri-twitter-x-line"></i>
                  X
                </a>
                <a
                  href={`https://www.douban.com/search?q=${encodeURIComponent(movie.title)}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex h-8 items-center gap-1.5 rounded-md border border-background-300/70 px-2.5 font-label text-[11px] text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-200"
                >
                  <i className="ri-book-2-line"></i>
                  豆瓣
                </a>
                <button
                  type="button"
                  onClick={copyLink}
                  className="flex h-8 cursor-pointer items-center gap-1.5 rounded-md border border-background-300/70 px-2.5 font-label text-[11px] text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-200"
                >
                  <i className="ri-file-copy-line"></i>
                  {copied ? "已复制" : "复制链接"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <article className="lg:col-span-8">
            <div className="mb-5 flex items-center gap-3">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">剧情介绍</h2>
              <span className="h-px flex-1 bg-background-200/80"></span>
              <span className="font-label text-[10.5px] uppercase tracking-[0.2em] text-foreground-500">Synopsis</span>
            </div>

            <div className="space-y-4">
              {movie.synopsis.map((paragraph, index) => (
                <p key={index} className="text-[14px] leading-[2] text-foreground-600">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-background-200/80 bg-background-100 p-4 md:p-5">
              <h3 className="mb-3 flex items-center gap-2 text-[13px] font-semibold text-foreground-800">
                <i className="ri-price-tag-3-line text-primary-300"></i>
                影片标签
              </h3>
              <div className="flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-md bg-secondary-100 px-2.5 py-1 text-[11.5px] text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <h3 className="mb-3 mt-6 flex items-center gap-2 text-[13px] font-semibold text-foreground-800">
                <i className="ri-folder-open-line text-accent-300"></i>
                所属分类
              </h3>
              <div className="flex flex-wrap gap-2">
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${genre}`}
                    className="inline-flex items-center gap-1 rounded-md border border-background-300/70 px-2.5 py-1.5 text-[11.5px] text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-200"
                  >
                    {genre}电影
                    <i className="ri-arrow-right-s-line"></i>
                  </Link>
                ))}
              </div>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200/80 bg-background-100 p-4 md:p-5">
              <h2 className="mb-4 flex items-center gap-2 text-[13px] font-semibold text-foreground-800">
                <i className="ri-file-list-3-line text-primary-300"></i>
                影片资料
              </h2>
              <dl className="divide-y divide-background-200/70">
                {[
                  { label: "原名", value: movie.originalTitle },
                  { label: "导演", value: movie.director },
                  { label: "主演", value: movie.cast.join("、") },
                  { label: "类型", value: movie.genres.join(" / ") },
                  { label: "地区", value: movie.country },
                  { label: "年份", value: `${movie.year}` },
                  { label: "片长", value: `${movie.duration} 分钟` },
                  { label: "评分", value: `${movie.rating.toFixed(1)} / 10` },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3 py-2.5">
                    <dt className="w-12 shrink-0 font-label text-[11px] text-foreground-500">{row.label}</dt>
                    <dd className="min-w-0 flex-1 text-[12px] leading-relaxed text-foreground-700">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {categoryInfo && (
              <div className="mt-4 rounded-lg border border-accent-800/50 bg-accent-950 p-4 md:p-5">
                <p className="font-label text-[10.5px] uppercase tracking-[0.2em] text-accent-300">Category note</p>
                <h3 className="mt-2 font-heading text-[15px] font-semibold text-foreground-900">
                  关于{categoryInfo.label}分类
                </h3>
                <p className="mt-2 text-[12px] leading-[1.9] text-foreground-500">{categoryInfo.intro}</p>
                <Link
                  to={`/category/${categoryInfo.key}`}
                  className="mt-4 inline-flex items-center gap-1 font-label text-[11.5px] text-secondary-300 transition-colors hover:text-secondary-200"
                >
                  浏览该分类全部作品
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            )}

            <div className="mt-4 rounded-lg border border-background-200/80 bg-background-50 p-4">
              <p className="text-[11.5px] leading-relaxed text-foreground-500">
                <i className="ri-information-line mr-1 text-primary-300"></i>
                本站仅提供影片资料与剧情介绍，不提供播放与下载。影片信息为演示用虚构内容，请支持正版发行。
              </p>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-14">
            <div className="mb-6 flex items-end justify-between">
              <div>
                <p className="mb-2 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.28em] text-primary-300">
                  <span className="h-px w-6 bg-primary-500/70"></span>
                  You may also like
                </p>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-[24px]">相关推荐</h2>
              </div>
              <Link
                to={`/category/${mainGenre}`}
                className="hidden items-center gap-1 whitespace-nowrap rounded-md border border-background-300/70 px-3.5 py-2 font-label text-[11.5px] text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-200 sm:inline-flex"
              >
                更多{mainGenre}作品
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:grid-cols-3 lg:grid-cols-4">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </section>
        )}
      </div>

      {copied && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 animate-fade-in rounded-md border border-primary-600 bg-background-100 px-4 py-2.5 text-[12px] text-foreground-800">
          <i className="ri-check-line mr-1.5 text-primary-300"></i>
          页面链接已复制
        </div>
      )}
    </div>
  );
}